import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  message,
  Upload
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import type { ColumnsType } from "antd/lib/table";
import { Link } from "react-router-dom";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { ranksApi } from "../../_helpers/apiService";

interface DataType {
  key: string;
  name: string;
  order: number;
  min_point?: number;
  max_point?: number;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 }
};

const deleteRank = async (rank: any, fetchRanks: () => void) => {
  try {
    await ranksApi.delete(rank.id);
    message.success(`"${rank.name}" цол амжилттай устгалаа.`);
    fetchRanks();
  } catch (error: any) {
    console.error("Delete rank error:", error);
    message.error("Цол устгахад алдаа гарлаа");
  }
};

function Ranks() {
  const [form] = Form.useForm();

  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.authentication.userData);

  useEffect(() => {
    fetchRanks();
  }, []);

  const fetchRanks = async () => {
    setLoading(true);
    try {
      const response = await ranksApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000,
        sort_by: 'order',
        sort_order: 'asc'
      });

      const ranks = response.data.items.map((rank: any) => ({
        ...rank,
        key: rank.id.toString(),
      }));

      setData(ranks);

      // Update Redux store with the latest ranks
      const ranksObject: any = {};
      response.data.items.forEach((rank: any) => {
        ranksObject[rank.id] = rank;
      });
      dispatch({ type: "SET_RANKS", payload: ranksObject });
    } catch (error: any) {
      console.error("Fetch ranks error:", error);
      message.error("Цолын мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleOk = () => {
    setConfirmLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        try {
          await ranksApi.create({
            name: values.name,
            order: parseInt(values.order),
            min_point: values.min_point ? parseInt(values.min_point) : undefined,
            max_point: values.max_point ? parseInt(values.max_point) : undefined,
            is_rank: false,
            club_id: CURRENT_CLUB_ID,
          });

          form.resetFields();
          setConfirmLoading(false);
          setVisible(false);
          message.success("Цол амжилттай нэмлээ");
          fetchRanks();
        } catch (error: any) {
          console.error("Create rank error:", error);
          message.error("Цол нэмэхэд алдаа гарлаа");
          setConfirmLoading(false);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setConfirmLoading(false);
      });
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => <>{index + 1}</>
    },
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (value, record: any) => <Space size="middle">{value}</Space>
    },
    {
      title: "Дугаар",
      dataIndex: "order",
      key: "order",
      width: 100,
      render: (value, record: any) => <Space size="middle">{value}</Space>,
      defaultSortOrder: "ascend",
      sorter: (a: any, b: any) => a.order - b.order
    },
    {
      title: "Min point",
      dataIndex: "min_point",
      key: "min_point",
      width: 120,
      render: (value, record: any) => <Space size="middle">{value || "-"}</Space>
    },
    {
      title: "Max point",
      dataIndex: "max_point",
      key: "max_point",
      width: 120,
      render: (value, record: any) => <Space size="middle">{value || "-"}</Space>
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/ranks/${record.key}`} key={record.key}>
            Засах
          </Link>
          <Popconfirm
            title="Устгахдаа итгэлтэй байна уу?"
            onConfirm={() => deleteRank(record, fetchRanks)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <Button type="link">Устгах</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <PageTitle
        title="Цол"
        description="Цолын жагсаалт"
        right={
          userData.role === "leader" ? (
            <> </>
          ) : (
            <Button
              shape="round"
              onClick={() => setVisible(true)}
              icon={<PlusOutlined />}
              size="large"
            >
              Цол нэмэх
            </Button>
          )
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      rowKey={(item) => item.key}
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Цол нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Дугаар"
            name="order"
            rules={[{ required: true, message: "Дугаар оруулна уу!" }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Min point"
            name="min_point"
            rules={[{ required: false, message: "Min point оруулна уу!" }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            label="Max point"
            name="max_point"
            rules={[{ required: false, message: "Max point оруулна уу!" }]}
          >
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Ranks;
